import { observer } from 'mobx-react-lite';
import moment from 'moment';
import numeral from 'numeral';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type { Recommendation, TTDCampaign } from '@feathr/blackbox';
import type { ICampaignValidationErrors } from '@feathr/extender/App/EventsPage/CampaignSummary/CampaignSummary.types';
import { useUser } from '@feathr/extender/state';
import { TimeFormat } from '@feathr/hooks';

import ExtendEndDate from './ExtendEndDate';
import IncreaseBudget from './IncreaseBudget';
import RecommendationModal from './RecommendationModal';

interface IProps {
  campaign: TTDCampaign;
  errors?: ICampaignValidationErrors;
  recommendation: Recommendation;
}

function IncreaseBudgetExtendEndDate({
  campaign,
  errors,
  recommendation,
}: Readonly<IProps>): JSX.Element {
  const { t } = useTranslation();
  const user = useUser();
  const timezone = user.get('timezone');

  const recommendedDate = recommendation.get('proposed_campaign_values').date_end;
  const recommendedBudget = recommendation.get('proposed_campaign_values').exposure_settings
    ?.target_value;

  const recommendedDateFormatted = moment
    .utc(recommendedDate)
    .tz(timezone)
    .format(TimeFormat.pickerShortDateTimeZone);
  const recommendedBudgetFormatted = numeral(recommendedBudget).format('$0');

  const prevDate = recommendation.get('current_campaign_values').date_end;
  const prevBudget = recommendation.get('current_campaign_values').exposure_settings?.target_value;

  const prevDateFormatted = moment
    .utc(prevDate)
    .tz(timezone)
    .format(TimeFormat.pickerShortDateTimeZone);
  const prevBudgetFormatted = numeral(prevBudget).format('$0');

  // TODO: make this more reusable when th recommendation structure is more clear
  const alerts = [
    t('Extend end date from {{before}} to {{after}}', {
      before: prevDateFormatted,
      after: recommendedDateFormatted,
    }),
    t('Increase budget from {{before}} to {{after}}', {
      before: prevBudgetFormatted,
      after: recommendedBudgetFormatted,
    }),
  ];

  const budgetErrors = errors?.budget ?? [];
  const endDateErrors = errors?.duration ?? [];

  return (
    <>
      <RecommendationModal.Alert description={alerts} />
      <ExtendEndDate
        campaign={campaign}
        errors={endDateErrors}
        recommendation={recommendation}
        suppressAlert={true}
      />
      <IncreaseBudget
        campaign={campaign}
        errors={budgetErrors}
        recommendation={recommendation}
        suppressAlert={true}
      />
    </>
  );
}

export default observer(IncreaseBudgetExtendEndDate);
